'use client'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'

export type TooltipProviderProps = ComponentProps<typeof TooltipPrimitive.Provider>

function TooltipProvider({ delayDuration = 0, ...props }: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

export type TooltipProps = ComponentProps<typeof TooltipPrimitive.Root>

function Tooltip({ ...props }: TooltipProps) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  )
}

export type TooltipTriggerProps = ComponentProps<typeof TooltipPrimitive.Trigger>

function TooltipTrigger({ ...props }: TooltipTriggerProps) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" type="button" {...props} />
}

export type TooltipContentVariant =
  | 'default'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'periwinkle'

export type TooltipContentProps = ComponentProps<typeof TooltipPrimitive.Content> & {
  variant?: TooltipContentVariant | null
}

function TooltipContent({
  className = '',
  sideOffset = 0,
  children,
  variant = 'default',
  ...props
}: TooltipContentProps) {
  const tooltipContentVariants = cva(
    'z-100 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-[6px] px-3 py-1.5 text-xs text-balance shadow-lg',
    {
      variants: {
        variant: {
          default: 'bg-brand-ink text-white',
          secondary: 'bg-secondary text-secondary-foreground',
          success: 'bg-success text-success-foreground',
          warning: 'bg-warning text-warning-foreground',
          destructive: 'bg-destructive text-destructive-foreground',
          periwinkle: 'bg-accent text-accent-foreground',
        },
      },
      defaultVariants: {
        variant: 'default',
      },
    }
  )

  // Get the background color for the arrow based on variant
  const getArrowColor = (variant: TooltipContentVariant | null) => {
    switch (variant) {
      case 'secondary':
        return 'bg-secondary fill-secondary'
      case 'success':
        return 'bg-success fill-success'
      case 'warning':
        return 'bg-warning fill-warning'
      case 'destructive':
        return 'bg-destructive fill-destructive'
      case 'periwinkle':
        return 'bg-accent fill-accent'
      case 'default':
      case null:
      default:
        return 'bg-brand-ink fill-brand-ink'
    }
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          tooltipContentVariants({ variant }),
          'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(
            getArrowColor(variant),
            'z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]'
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
